import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircleOutline as CompletoIcon, ExpandMore as ExpandIcon } from "@mui/icons-material";
import EstadoChip from "../kit/EstadoChip";
import Etiqueta from "../kit/Etiqueta";
import { useExpedientesGuardados, useGuardarExpedientes } from "../../queries/useExpedientes";
import { expedienteService } from "../../services/expedienteService";
import { tokens } from "../../theme";
import { ResultadoExpediente } from "../../types/expediente";
import { mensajeDeError } from "../../utils/errores";
import {
  FichaEstudiante,
  FilaEstudiante,
  GrupoCarrera,
  filasDeEstudiantes,
  hayPendientes,
  nombreCarrera,
  porCarrera,
  porEstudiante,
  soloConPendientes,
} from "../../utils/expediente";
import { tiempoTranscurrido } from "../../utils/fecha";
import { BOLETIN_URL, MODULO_URL, expedienteUrl } from "../../utils/ucampus";

/** "1 pendiente" / "3 pendientes": el chip queda al lado del nombre y se lee como frase. */
const cuenta = (n: number, palabra: string) => `${n} ${palabra}${n === 1 ? "" : "s"}`;

const AYUDA_COOKIE =
  "Abre UCampus con tu sesión, entra a las herramientas del navegador (pestaña Red, cualquier petición a ucampus.uchile.cl) y copia el encabezado Cookie. También sirve pegar solo el valor de _fcfm.";

export interface EstudianteAConsultar {
  rut: number;
  nombre: string;
}

/**
 * Revisar el expediente de titulación de UCampus de los estudiantes de un paso.
 *
 * UCampus muestra el expediente de a una persona, así que ver cómo va el paso
 * completo obligaba a abrir una pestaña por estudiante. Acá se consultan todos
 * de una y el resultado se lee al revés: por trámite, con cuántos lo tienen
 * cerrado y quiénes no.
 *
 * Dos reglas del diálogo:
 *
 * - **Consultar no es guardar.** El resultado queda en pantalla; se escribe solo
 *   al apretar "Guardar", para que mirar el expediente de una persona suelta no
 *   toque la foto del resto.
 * - **No se pregunta dos veces lo mismo.** Quien ya fue consultado hoy viene sin
 *   marcar, con la antigüedad a la vista; para volver a preguntar hay que decirlo.
 */
export default function ExpedienteDialog({
  paso,
  estudiantes,
  onClose,
}: {
  paso: string;
  estudiantes: EstudianteAConsultar[];
  onClose: () => void;
}) {
  const ruts = useMemo(() => estudiantes.map((e) => e.rut), [estudiantes]);
  const { data: guardado, isLoading } = useExpedientesGuardados(ruts);
  const guardarMut = useGuardarExpedientes();

  const [cookie, setCookie] = useState("");
  const [elegidos, setElegidos] = useState<number[]>([]);
  const [reconsultar, setReconsultar] = useState(false);
  // Por defecto la lista de trámites muestra solo lo que le falta a alguien.
  const [verSinPendientes, setVerSinPendientes] = useState(false);
  const [resultados, setResultados] = useState<ResultadoExpediente[] | null>(null);
  const [avance, setAvance] = useState<{ hecho: number; total: number; nombre: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const previos = useMemo(() => new Map((guardado?.resultados ?? []).map((r) => [r.rut, r])), [guardado]);

  // Marca inicial: todos menos los consultados hoy. Con "volver a consultar",
  // todos. Se recalcula al llegar lo guardado y al cambiar el switch.
  useEffect(() => {
    if (isLoading) return;
    setElegidos(estudiantes.filter((e) => reconsultar || !previos.get(e.rut)?.fresco).map((e) => e.rut));
  }, [isLoading, previos, estudiantes, reconsultar]);

  const alternar = (rut: number) => setElegidos((prev) => (prev.includes(rut) ? prev.filter((r) => r !== rut) : [...prev, rut]));

  /** Encadena la consulta de a uno: UCampus responde de a uno y así se ve el avance. */
  const extraer = async () => {
    const aConsultar = estudiantes.filter((e) => elegidos.includes(e.rut));
    setError(null);
    setResultados(null);
    const juntados: ResultadoExpediente[] = [];

    for (const [i, estudiante] of aConsultar.entries()) {
      setAvance({ hecho: i, total: aConsultar.length, nombre: estudiante.nombre });
      try {
        const resultado = await expedienteService.extraer(estudiante.rut, cookie);
        juntados.push({ ...resultado, nombre: resultado.nombre || estudiante.nombre });
        if (resultado.detener) {
          // La cookie ya no sirve: seguir con el resto son N errores iguales.
          setError(resultado.error ?? "La sesión de UCampus ya no es válida.");
          break;
        }
      } catch (e) {
        setError(mensajeDeError(e, "No se pudo consultar UCampus."));
        break;
      }
    }

    setAvance(null);
    setResultados(juntados);
  };

  const guardar = () =>
    guardarMut.mutate(
      (resultados ?? []).filter((r) => !r.error),
      { onSuccess: onClose },
    );

  const consultando = avance !== null;
  const conDatos = resultados ?? guardado?.resultados ?? [];
  const filas = useMemo(() => filasDeEstudiantes(conDatos), [conDatos]);
  const carreras = useMemo(() => porCarrera(filas), [filas]);
  const fichas = useMemo(() => porEstudiante(filas), [filas]);
  const fallidos = filas.filter((f) => f.error).length;
  const sinGuardar = (resultados ?? []).some((r) => !r.error);

  return (
    <Dialog open onClose={consultando ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Expediente de titulación — {paso}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Consulta en UCampus el expediente de cada estudiante y los junta por trámite. Consultar no guarda: el resultado se
            escribe solo si aprietas «Guardar».
          </Typography>

          {!resultados && (
            <>
              <TextField
                label="Cookie de sesión de UCampus"
                value={cookie}
                onChange={(e) => setCookie(e.target.value)}
                placeholder="_fcfm=g2t3rgn8smmja443jvkbnt73f9; SESSID_upasaporte=…"
                helperText={AYUDA_COOKIE}
                fullWidth
                multiline
                minRows={2}
                disabled={consultando}
              />

              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" useFlexGap>
                  <Typography variant="caption" color="text.secondary">
                    A quiénes consultar ({elegidos.length} de {estudiantes.length}):
                  </Typography>
                  <Button size="small" onClick={() => setElegidos(elegidos.length ? [] : ruts)} disabled={consultando}>
                    {elegidos.length ? "Ninguno" : "Todos"}
                  </Button>
                </Stack>

                <Stack sx={{ mt: 0.5, maxHeight: 280, overflowY: "auto" }} divider={<Divider flexItem />}>
                  {estudiantes.map((e) => {
                    const previo = previos.get(e.rut);
                    const edad = tiempoTranscurrido(previo?.consultado_en);
                    return (
                      <FormControlLabel
                        key={e.rut}
                        control={
                          <Checkbox size="small" checked={elegidos.includes(e.rut)} onChange={() => alternar(e.rut)} disabled={consultando} />
                        }
                        label={
                          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Typography variant="body2">{e.nombre}</Typography>
                            {edad && (
                              <Etiqueta variante={previo?.fresco ? "acento" : "contorno"}>
                                extraído {edad}
                                {previo?.expedientes[0] ? ` · ${previo.expedientes[0].completos}/${previo.expedientes[0].total}` : ""}
                              </Etiqueta>
                            )}
                          </Stack>
                        }
                        sx={{ py: 0.25, mr: 0 }}
                      />
                    );
                  })}
                </Stack>

                {[...previos.values()].some((p) => p.fresco) && (
                  <FormControlLabel
                    sx={{ mt: 0.5 }}
                    control={<Checkbox size="small" checked={reconsultar} onChange={(e) => setReconsultar(e.target.checked)} disabled={consultando} />}
                    label={
                      <Typography variant="body2">
                        Volver a consultar a quienes ya se extrajeron hoy
                        <Typography component="span" variant="caption" color="text.secondary" display="block">
                          El expediente lo mueven personas a mano: rara vez cambia dos veces el mismo día.
                        </Typography>
                      </Typography>
                    }
                  />
                )}
              </Box>
            </>
          )}

          {consultando && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Consultando {avance.hecho + 1} de {avance.total} · {avance.nombre}
              </Typography>
              <LinearProgress variant="determinate" value={(avance.hecho / avance.total) * 100} sx={{ mt: 0.75, borderRadius: 1 }} />
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}
          {guardarMut.isError && <Alert severity="error">{mensajeDeError(guardarMut.error, "No se pudo guardar.")}</Alert>}

          {!resultados && !isLoading && filas.length > 0 && (
            <Alert severity="info">
              Se muestra lo último que se extrajo. Vuelve a consultar para actualizarlo.
            </Alert>
          )}

          {filas.length > 0 && (
            <>
              {fallidos > 0 && (
                <Alert severity="warning">
                  {fallidos === 1 ? "1 estudiante no se pudo consultar" : `${fallidos} estudiantes no se pudieron consultar`}; su expediente
                  anterior queda como estaba.
                </Alert>
              )}
              <ResumenProcesos carreras={carreras} verSinPendientes={verSinPendientes} onVerSinPendientes={setVerSinPendientes} />
              <PorEstudiante fichas={fichas} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <Button onClick={onClose} disabled={consultando || guardarMut.isPending}>
          {sinGuardar ? "Cerrar sin guardar" : "Cerrar"}
        </Button>
        <Stack direction="row" spacing={1}>
          <Button onClick={extraer} disabled={consultando || !cookie.trim() || elegidos.length === 0}>
            {consultando ? "Consultando…" : resultados ? "Consultar de nuevo" : "Extraer expediente"}
          </Button>
          <Button variant="contained" onClick={guardar} disabled={!sinGuardar || consultando || guardarMut.isPending}>
            {guardarMut.isPending ? "Guardando…" : "Guardar"}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

/**
 * El expediente al revés: una fila por trámite, con cuántos lo tienen pendiente.
 *
 * Separado por carrera, porque la licenciatura y el título son trámites
 * distintos y contarlos juntos daría un número que no es de nadie. Dentro de
 * cada carrera va el orden del propio expediente: primero la sección, después la
 * posición en la tabla.
 *
 * Por defecto se lista solo lo que le falta a alguien: un trámite que nadie debe
 * no tiene a quién ir a buscar, y una sección entera cerrada —"Requisitos
 * Académicos" cuando todos los aprobaron— desaparece en vez de ocupar pantalla.
 * El checkbox trae de vuelta la tabla completa para cuando se quiere comprobar
 * que un trámite está cerrado, no solo enterarse de que falta.
 */
function ResumenProcesos({
  carreras,
  verSinPendientes,
  onVerSinPendientes,
}: {
  carreras: GrupoCarrera[];
  verSinPendientes: boolean;
  onVerSinPendientes: (valor: boolean) => void;
}) {
  if (!carreras.length) return null;
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
          Por trámite
        </Typography>
        <FormControlLabel
          control={<Checkbox size="small" checked={verSinPendientes} onChange={(e) => onVerSinPendientes(e.target.checked)} />}
          label={<Typography variant="caption">Mostrar los trámites que no le faltan a nadie</Typography>}
          sx={{ mr: 0 }}
        />
      </Stack>
      {carreras.map((carrera, i) => (
        // Las carreras son bloques independientes: el aire entre ellas es lo que
        // evita leer los trámites de una como continuación de la otra.
        <Box key={carrera.clave} sx={{ mt: i === 0 ? 1.5 : 4 }}>
          <EncabezadoCarrera carrera={carrera.carrera} expedientes={carrera.filas.length} />
          {!hayPendientes(carrera.secciones) && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
              <CompletoIcon fontSize="small" sx={{ color: tokens.chip.exito.fg }} />
              <Typography variant="body2" color="text.secondary">
                Nadie tiene trámites pendientes en esta carrera.
              </Typography>
            </Stack>
          )}
          {(verSinPendientes ? carrera.secciones : soloConPendientes(carrera.secciones)).map(({ grupo, procesos }) => (
            <Box key={grupo} sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.disabled" sx={{ display: "block", mb: 0.5 }}>
                {grupo}
              </Typography>
              {procesos.map((p) => {
                const faltan = p.pendientes.length;
                return (
                  <Accordion
                    key={p.slug}
                    disableGutters
                    elevation={0}
                    square={false}
                    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, "&:before": { display: "none" }, mb: 0.5 }}
                  >
                    <AccordionSummary
                      expandIcon={faltan ? <ExpandIcon /> : null}
                      // Sin pendientes no hay nada que desplegar: el resumen deja de
                      // comportarse como botón para no ofrecer una acción vacía.
                      sx={{ minHeight: 44, cursor: faltan ? "pointer" : "default" }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%", pr: 1 }} flexWrap="wrap" useFlexGap>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: faltan ? 600 : 400, flexGrow: 1 }}
                          color={faltan ? "text.primary" : "text.disabled"}
                        >
                          {p.nombre}
                        </Typography>
                        {faltan > 0 && <EstadoChip label={cuenta(faltan, "pendiente")} tono="aviso" denso />}
                      </Stack>
                    </AccordionSummary>
                    {faltan > 0 && (
                      <AccordionDetails sx={{ pt: 0 }}>
                        <Stack spacing={0.5}>
                          {p.pendientes.map((f) => (
                            <Stack
                              key={f.clave}
                              direction="row"
                              spacing={1}
                              alignItems="baseline"
                              justifyContent="space-between"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Typography variant="caption" color="text.secondary">
                                {f.nombre}
                                {resultadoDe(f, p.slug) ? ` — ${resultadoDe(f, p.slug)}` : ""}
                              </Typography>
                              <EnlacesUCampus rut={f.rut} carrCodigo={f.carrCodigo} />
                            </Stack>
                          ))}
                        </Stack>
                      </AccordionDetails>
                    )}
                  </Accordion>
                );
              })}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}

/**
 * Los dos saltos a UCampus que siguen a ver un pendiente: el boletín de notas y
 * el expediente mismo. Abren en otra pestaña porque el diálogo tiene resultados
 * sin guardar y navegar fuera los perdería.
 *
 * El expediente necesita la carrera además del RUT: sin `carr_codigo` UCampus no
 * sabe cuál de los dos expedientes de la persona mostrar.
 */
function EnlacesUCampus({ rut, carrCodigo }: { rut: number; carrCodigo: number | null }) {
  const expediente = expedienteUrl(rut, carrCodigo);
  return (
    <Stack direction="row" spacing={1.25} component="span" sx={{ flexShrink: 0 }}>
      {/* `marca.texto` y no el primario de MUI: el azul de marca como texto no se
          lee sobre fondo oscuro (ver docs/DESIGN_KIT.md, "Accesibilidad"). */}
      <Link href={BOLETIN_URL} target="_blank" rel="noreferrer" variant="caption" underline="hover" sx={{ color: tokens.marca.texto }}>
        Ver boletín
      </Link>
      <Link href={expediente} target="_blank" rel="noreferrer" variant="caption" underline="hover" sx={{ color: tokens.marca.texto }}>
        Ver expediente
      </Link>
    </Stack>
  );
}

/** Lo que UCampus dice de ese trámite para esa persona ("Sin información", …). */
const resultadoDe = (fila: FilaEstudiante, slug: string) => fila.procesos.find((p) => p.slug === slug)?.resultado ?? "";

/** Encabezado de una carrera: separa los dos expedientes que puede tener alguien. */
function EncabezadoCarrera({ carrera, expedientes }: { carrera: string; expedientes: number }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="baseline"
      flexWrap="wrap"
      useFlexGap
      sx={{ borderBottom: "1px solid", borderColor: "divider", pb: 0.75 }}
    >
      <Typography variant="subtitle1" sx={{ color: tokens.marca.texto }}>
        {carrera || "Sin carrera"}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {expedientes === 1 ? "1 expediente" : `${expedientes} expedientes`}
      </Typography>
    </Stack>
  );
}

/**
 * Una ficha por persona: el nombre arriba y debajo lo que le falta.
 *
 * Quien tiene dos carreras aparece una sola vez, con un expediente bajo el otro:
 * la pregunta acá es "a esta persona qué le falta", y la respuesta se lee entera
 * sin tener que juntar dos filas separadas de la lista.
 */
function PorEstudiante({ fichas }: { fichas: FichaEstudiante[] }) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
        Por estudiante
      </Typography>
      {/* Sin scroll propio: ahora cada estudiante es un bloque de varias líneas y
          una caja con su propia barra dentro del diálogo se vuelve incómoda. */}
      <Stack sx={{ mt: 0.5 }} divider={<Divider flexItem />}>
        {fichas.map((ficha) => (
          <Box key={ficha.rut} sx={{ py: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {ficha.nombre}
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 0.5, pl: 1.5, borderLeft: "2px solid", borderColor: "divider" }}>
              {ficha.expedientes.map((f) => (
                <ExpedienteDeLaFicha key={f.clave} fila={f} conCarrera={ficha.expedientes.length > 1} />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
      <Typography variant="caption" color="text.disabled" sx={{ display: "block", mt: 1 }}>
        El expediente vive en{" "}
        <Link href={MODULO_URL} target="_blank" rel="noreferrer" sx={{ color: tokens.marca.texto }}>
          Títulos y Grados de UCampus
        </Link>
        ; acá solo se lee.
      </Typography>
    </Box>
  );
}

/** Un expediente dentro de la ficha: su estado y la lista de trámites que faltan. */
function ExpedienteDeLaFicha({ fila, conCarrera }: { fila: FilaEstudiante; conCarrera: boolean }) {
  if (fila.error) {
    return (
      <Typography variant="caption" color="error.main">
        {fila.error}
      </Typography>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
        {conCarrera && (
          <Typography variant="caption" sx={{ fontWeight: 600, color: tokens.marca.texto }}>
            {nombreCarrera(fila.carrera)}
          </Typography>
        )}
        {fila.estado && <Etiqueta variante="contorno">{fila.estado}</Etiqueta>}
        {fila.nota && <Etiqueta variante="marca">{fila.nota}</Etiqueta>}
        {fila.pendientes.length > 0 && <EstadoChip label={cuenta(fila.pendientes.length, "pendiente")} tono="aviso" denso />}
        <Box sx={{ ml: { sm: "auto" } }}>
          <EnlacesUCampus rut={fila.rut} carrCodigo={fila.carrCodigo} />
        </Box>
      </Stack>

      {fila.pendientes.length === 0 ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
          Expediente completo
        </Typography>
      ) : (
        <Stack sx={{ mt: 0.25 }}>
          {fila.pendientes.map((p) => (
            <Typography key={p.slug} variant="caption" color="text.secondary">
              {p.nombre}
              {p.responsable ? ` · ${p.responsable}` : ""}
            </Typography>
          ))}
        </Stack>
      )}
    </Box>
  );
}
